import React, { useEffect, useState, useCallback } from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "../../../components/AppToast";
import CommonDataTable from "../../../components/CommonDataTable";
import { useSelector } from "react-redux";
import {
  columnLabels,
  defaultVisibleFields,
  getColumnWidth,
  getValueFormatter,
  getColumnLabel
} from "../constant/FacilityListColumns";
import { getallfacility } from "../../../services/facility/facility";

const startCase = (str) =>
  (str || "")
    .replace(/([A-Z]+)/g, " $1")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bAsn\b/gi, "ASN")
    .replace(/\bSku\b/gi, "SKU")
    .replace(/\bId\b/gi, "ID")
    .trim();

export default function FacilityList({
  onViewAsn,
  refreshKey,
  onEditAsn,
  showCreateButton = false,
  createButtonText = "Create New Partner",
  createButtonIcon = "bi-plus-lg",
  onCreateClick
}) {
  const {userDetail} = useSelector((state)=>state.login)
  const [allPossibleColumns, setAllPossibleColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const loadList = useCallback(async (pageNo , pageSizeNo) => {
    setLoading(true);
    try {
      const res = await getallfacility(pageNo , pageSizeNo);
      const total = res.total;

      const apiData = res.data;

      const fieldSet = new Set();

      const builtRows = (apiData || []).map((rec, idx) => {
        const addr = rec.Facility_Address || {};
        const mgr = rec.Facility_ManagerDetails || {};
        const contact = rec.Facility_ManagerContact || {};
        const opDays = rec.Facility_OperationalDaysAndHours || [];

        // Flatten operational hours as string
        const operationalHours = opDays
          .map(
            (d) =>
              `${d.dayRange}: ${d.timeRange?.open || ""}–${d.timeRange?.close || ""}`
          )
          .join(", ");

        // Ensure all fields exist (stringify numeric values)
        const flat = {
          id: rec._id || idx,
          _id: rec._id,
          Facility_ID: rec.Facility_ID || "",
          Facility_Name: rec.Facility_Name || "",
          Facility_Type: rec.Facility_Type || "",
          Facility_Description: rec.Facility_Description || "",
          Facility_Owner_Type: rec.Facility_Owner_Type || "",
          "Facility_Address.address1": addr.address1 || "",
          "Facility_Address.address2": addr.address2 || "",
          "Facility_Address.city": addr.city || "",
          "Facility_Address.state": addr.state || "",
          "Facility_Address.pincode": addr.pincode || "",
          "Facility_Address.country": addr.country || "",
          Facility_Capacity:
            rec.Facility_Capacity !== undefined ? String(rec.Facility_Capacity) : "",
          Facility_StorageType: rec.Facility_StorageType || "",
          Facility_DockDetails: rec.Facility_DockDetails || "",
          Facility_OtherDetails: rec.Facility_OtherDetails || "",
          "Facility_ManagerDetails.firstName": mgr.firstName || "",
          "Facility_ManagerDetails.lastName": mgr.lastName || "",
          "Facility_ManagerContact.mobile":
            contact.mobile !== undefined ? String(contact.mobile) : "",
          "Facility_ManagerContact.alternateMobile":
            contact.alternateMobile !== undefined ? String(contact.alternateMobile) : "",
          "Facility_ManagerContact.email": contact.email || "",
          operationalHours,
          tenantId: rec.tenantId || "",
          partnerId: rec.partnerId || "",
          status: rec.status === 1 ? "Active" : "Inactive",
          createdBy: rec.createdBy || "",
          updatedBy: rec.updatedBy || "",
          createdAt: rec.createdAt
            ? new Date(rec.createdAt).toLocaleString()
            : "",
          updatedAt: rec.updatedAt
            ? new Date(rec.updatedAt).toLocaleString()
            : ""
        };


        Object.keys(flat).forEach((k) => {
          if (k !== "id" && k !== "__raw") fieldSet.add(k);
        });

        return flat;
      });

      // Add default columns if missing
      defaultVisibleFields.forEach((f) => fieldSet.add(f));

      // Arrange columns: defaults first, then others alphabetically
      const remaining = Array.from(fieldSet)
        .filter((f) => !defaultVisibleFields.includes(f))
        .sort((a, b) => a.localeCompare(b));

      const orderedFields = [...defaultVisibleFields, ...remaining];

      // Actions column
      const actionsColumn = {
        field: "__actions",
        headerName: columnLabels["__actions"] || "",
        width: 20,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <IconButton size="small" onClick={(e) => handleOpenMenu(e, params.row)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )
      };

      const cols = [
        userDetail.userType!=='PARTNER'?actionsColumn:'',
        ...orderedFields.map((f) => ({
          field: f,
          headerName: getColumnLabel(f, startCase),
          width: getColumnWidth(f),
          valueFormatter: getValueFormatter(f)
        }))
      ];

      setAllPossibleColumns(cols);
      setRows(builtRows);
      setTotalCount(total);
    } catch (error) {
      toast.error(
        `Failed to load Facility data: ${error.message || "Unknown error"}`
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    loadList(page, pageSize);
  }, [loadList, refreshKey,page, pageSize]);

  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuRow(row);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  const handleView = () => {
    if (menuRow && onViewAsn) {
      onViewAsn(menuRow);
    }
    handleCloseMenu();
  };

  const handleEdit = () => {
    if (menuRow && onEditAsn) {
      onEditAsn(menuRow);
    }
    handleCloseMenu();
  };

  return (
    <>
      {loading && <div className="text-center py-3">Loading Facility data...</div>}

      <CommonDataTable
        id="partner-table"
        allPossibleColumns={allPossibleColumns}
        initialVisible={["__actions", ...defaultVisibleFields, "lineItemsCount"]}
        initialRows={rows}
        height={500}
        rowsPerPageOptions={[10,25, 50,100]}
        // onDataRefresh={loadList}
        checkboxSelection
        exportImportPath={"facility"}
        sampleFileUrl="/samples/facility.xlsx"
        sampleFileName="facility"
        showCreateButton={showCreateButton}
        createButtonText={createButtonText}
        createButtonIcon={createButtonIcon}
        onCreateClick={onCreateClick}
        enableImportButton={false}
         page={page}
        pageSize={pageSize}
        rowCount={totalCount}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(0);
        }}


        onDataRefresh={async ({ filters = [], page: p = 0, pageSize: ps = pageSize } = {}) => {
          // NOTE: this just forwards the values to your fetchHandler. For now we console.log as requested.
          console.log("ASN ....................... received onDataRefresh:", { filters, page: p, pageSize: ps });
          // call your existing fetchHandler (we'll update it to accept filters)
          // await loadList(p, ps, filters);
        }}
      />

      <Menu
        open={Boolean(menuAnchorEl)}
        anchorEl={menuAnchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem onClick={handleView}>
          <i className="bi bi-eye me-2"></i> View
        </MenuItem>

        <MenuItem onClick={handleEdit}>
          <i className="bi bi-pencil me-2"></i> Edit
        </MenuItem>
      </Menu>


    </>
  );
}
